import { useEffect, useState } from 'react'
import { History } from 'lucide-react'
import { useProductAudit, useUpdateProductVolume } from '@/hooks/useProducts'
import {
  PRODUCT_VOLUME_REASONS,
  type CreatedProduct,
  type ProductVolumeReason,
} from '@/types/products'
import {
  ReasonField,
  reasonIsComplete,
} from '@/components/procedure/ReasonField'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

/**
 * Correct a product's recorded volume, with a reason.
 *
 * Volume is not cosmetic — it drives dose calculations, the split maths that
 * scales LIMS totals, and what the label claims — and it changes physically
 * outside splits (QC sampling, transfer loss). So it has to be correctable,
 * and every correction has to say why.
 *
 * The existing trail is shown alongside the form rather than hidden behind
 * another click: an audit nobody can read is only half an audit, and seeing
 * the last correction is usually what tells an operator whether theirs is
 * even needed.
 */
export function UpdateProductVolumeDialog({
  product,
  onClose,
}: {
  product: CreatedProduct | null
  onClose: () => void
}) {
  const updateVolume = useUpdateProductVolume()
  const audit = useProductAudit(product?.id ?? null)

  const [volume, setVolume] = useState('')
  const [reason, setReason] = useState<ProductVolumeReason>('Sampling')
  const [detail, setDetail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Seed from the product each time the dialog opens on a different one, so
  // the field starts at today's figure rather than blank or a stale one.
  useEffect(() => {
    setVolume(product?.volumeMl != null ? String(product.volumeMl) : '')
    setReason('Sampling')
    setDetail('')
    setError(null)
  }, [product?.id, product?.volumeMl])

  const parsed = Number(volume)
  const volumeValid = volume.trim() !== '' && !Number.isNaN(parsed) && parsed >= 0
  const canSave = volumeValid && reasonIsComplete(reason, detail)

  async function handleSave() {
    if (!product || !canSave) return
    setSaving(true)
    setError(null)
    try {
      await updateVolume.mutateAsync({
        productId: product.id,
        volumeMl: parsed,
        reason,
        reasonDetail: detail.trim() || undefined,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update the volume.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!product} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Update volume</DialogTitle>
          <DialogDescription>
            {product ? `DIN ${product.din}` : ''} — record the corrected volume and why it
            changed.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='space-y-1'>
            <label className='text-sm font-medium' htmlFor='product-volume'>
              Volume (mL)
            </label>
            <Input
              id='product-volume'
              type='number'
              min={0}
              value={volume}
              onChange={e => setVolume(e.target.value)}
              autoFocus
            />
            {product?.volumeMl != null && (
              <p className='text-xs text-muted-foreground'>
                Currently recorded: {product.volumeMl} mL
              </p>
            )}
          </div>

          <ReasonField
            presets={PRODUCT_VOLUME_REASONS}
            reason={reason}
            detail={detail}
            onReasonChange={r => setReason(r as ProductVolumeReason)}
            onDetailChange={setDetail}
          />

          {audit.data && audit.data.length > 0 && (
            <div className='rounded-lg border bg-muted/20 p-3'>
              <p className='mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                <History className='h-3.5 w-3.5' />
                Previous changes
              </p>
              <ul className='space-y-1 text-xs text-muted-foreground'>
                {audit.data.slice(0, 5).map(entry => (
                  <li key={entry.id}>
                    {volumeOf(entry.oldValue) ?? '—'} → {volumeOf(entry.newValue) ?? '—'} mL
                    {entry.reason ? ` · ${entry.reason}` : ''} · {entry.changedBy} ·{' '}
                    {new Date(entry.changedAt).toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save volume'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** `oldValue`/`newValue` are free-form JSON; pull a volume out when there is one. */
function volumeOf(value: unknown): number | null {
  if (value && typeof value === 'object' && 'volumeMl' in value) {
    const v = (value as { volumeMl?: unknown }).volumeMl
    return typeof v === 'number' ? v : null
  }
  return null
}
